/** Magnet QA stores (same ids as quote/actions gates on CustomerDetailPageMagnet). */
export const MAGNET_TEST_STORE_IDS = [-1, -2, -3]

/**
 * Numeric store id for inbox / messaging APIs, or null if missing/invalid.
 * Allows Magnet test stores (-1, -2, -3) and positive ids; rejects 0 and NaN.
 * (Previously rejected -1, which disabled the inbox for test stores.)
 */
export const parseInboxStoreId = (storeId) => {
  if (storeId == null || storeId === '') {
    return null
  }
  const n = +storeId
  if (!Number.isFinite(n) || n === 0) {
    return null
  }
  return n
}

/** @deprecated Use parseInboxStoreId */
export const parsePositiveStoreId = parseInboxStoreId

const decodeParam = (raw) => {
  if (raw == null || raw === '') {
    return null
  }
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

const parseInactiveFlag = (raw) => {
  if (raw == null || raw === '') {
    return false
  }
  const normalized = String(raw).trim().toLowerCase()
  return normalized === '1' || normalized === 'true' || normalized === 'yes'
}

/**
 * @param {string} search - location.search (with or without leading ?)
 * @returns {{ customerId: string|null, storeId: string|null, customerInactive: boolean }}
 */
/**
 * @param {{ customerId: string, storeId?: string|number|null, inactive?: boolean }} opts
 * @returns {string} Search string with leading `?`, e.g. `?customer=46164&storeId=-1`
 */
export const buildCustomerMessagesPageSearch = ({
  customerId,
  storeId,
  inactive = false,
}) => {
  const params = new URLSearchParams()
  if (customerId != null && customerId !== '') {
    params.set('customer', String(customerId))
  }
  const parsedStore = storeId != null ? parseInboxStoreId(storeId) : null
  if (parsedStore != null) {
    params.set('storeId', String(parsedStore))
  }
  if (inactive === true) {
    params.set('inactive', '1')
  }
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const parseMessagesPageSearchParams = (search) => {
  const params = new URLSearchParams(
    search.startsWith('?') ? search : `?${search}`
  )
  const customerRaw = params.get('customer')
  const storeRaw = params.get('storeId')
  const customerId = decodeParam(customerRaw)
  const storeIdDecoded = decodeParam(storeRaw)
  const parsedStore =
    storeIdDecoded != null ? parseInboxStoreId(storeIdDecoded) : null
  const storeId = parsedStore != null ? String(parsedStore) : null
  return {
    customerId: customerId || null,
    storeId,
    customerInactive: parseInactiveFlag(params.get('inactive')),
  }
}

/**
 * Removes deep-link query params after the user browses the inbox
 * (customer, inactive, storeId). Account store becomes source of truth.
 * Returns '' or a string starting with '?'.
 *
 * @param {string} search - location.search (with or without leading ?)
 * @returns {string}
 */
export const stripMessagesDeepLinkCustomerParams = (search) => {
  const params = new URLSearchParams(
    search.startsWith('?') ? search : `?${search}`
  )
  params.delete('customer')
  params.delete('inactive')
  params.delete('storeId')
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

/**
 * Removes storeId from the messages page query when account store diverges from URL.
 *
 * @param {string} search - location.search (with or without leading ?)
 * @returns {string}
 */
export const stripMessagesPageStoreParam = (search) => {
  const params = new URLSearchParams(
    search.startsWith('?') ? search : `?${search}`
  )
  params.delete('storeId')
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export default parseMessagesPageSearchParams
